import { Howler } from 'howler';

/**
 * Audio Context Manager
 *
 * Handles browser AudioContext requirements and Howler.js global configuration
 *
 * IMPORTANT: Modern browsers require AudioContext to be initialized after a user gesture
 * This prevents auto-playing audio on page load (security policy)
 */

let audioContextInitialized = false;

/**
 * Configure Howler.js global settings
 * Should be called once on app initialization
 */
export function configureHowler(): void {
  // Increase HTML5 audio pool to prevent exhaustion
  // Higher pool size for apps with multiple audio instances
  Howler.html5PoolSize = 50;

  // Enable auto-unlock on user gesture (mobile Safari support)
  Howler.autoUnlock = true;

  // Set global volume from localStorage if available
  const savedVolume = localStorage.getItem('audioVolume');
  if (savedVolume) {
    Howler.volume(parseFloat(savedVolume));
  } else {
    Howler.volume(0.5); // Default 50%
  }

  // Log pool configuration for debugging
  if (import.meta.env.DEV) {
    console.log('🔊 Howler.js configured:', {
      html5PoolSize: Howler.html5PoolSize,
      autoUnlock: Howler.autoUnlock,
      volume: Howler.volume(),
    });
  }
}

/**
 * Initialize AudioContext after user gesture
 * Call this on first user interaction (button click, timer start, etc.)
 *
 * Returns true if initialization was successful or already initialized
 */
export function initializeAudioContext(): boolean {
  if (audioContextInitialized) {
    return true;
  }

  try {
    // Resume AudioContext if suspended (browser security policy)
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume();
    }

    audioContextInitialized = true;
    console.log('✅ AudioContext initialized successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize AudioContext:', error);
    return false;
  }
}

/**
 * Check if AudioContext is initialized
 */
export function isAudioContextInitialized(): boolean {
  return audioContextInitialized;
}

/**
 * Reset initialization state (useful for testing)
 */
export function resetAudioContext(): void {
  audioContextInitialized = false;
}

/**
 * Get AudioContext state for debugging
 */
export function getAudioContextState(): {
  initialized: boolean;
  state: AudioContextState | 'no-context';
  poolSize: number;
  autoUnlock: boolean;
} {
  return {
    initialized: audioContextInitialized,
    state: Howler.ctx?.state || 'no-context',
    poolSize: Howler.html5PoolSize,
    autoUnlock: Howler.autoUnlock,
  };
}
